import type { MouseEvent } from 'react';

import { CampaignProductType } from 'utils/constants/campaign';

export const CAMPAIGN_CREATION_WIZARD_HISTORY_STATE = {
    saveMainMenuData: true,
};

export const isCampaignCreationWizardHistoryState = (historyState: unknown): boolean =>
    Boolean(
        historyState &&
            typeof historyState === 'object' &&
            'saveMainMenuData' in historyState &&
            historyState.saveMainMenuData
    );

export enum CampaignCreationWizardSource {
    VacancyList = 'vacancyList',
    VacancyUrl = 'vacancyUrl',
    ExternalLink = 'externalLink',
}

export const enum CampaignCreationWizardStep {
    PromotionObject = 0,
    PromotionType = 1,
}

export type CampaignCreationWizardCpcMode = 'quick' | 'flexible';

export type CampaignCreationWizardProgress = { visible: false } | { visible: true; progress: number; segments: number };
export type VisibleCampaignCreationWizardProgress = Extract<CampaignCreationWizardProgress, { visible: true }>;

interface WizardProgressParams {
    activeStep: number;
    campaignTarget: string;
    vacanciesCount: number;
    vacanciesIsLoading: boolean;
    isFiltered: boolean;
    selectedVacancyId: number | null;
    selectedProductType: CampaignProductType | null;
    selectedCpcMode: CampaignCreationWizardCpcMode | null;
}

const VISIBLE_PROGRESS = (progress: number, segments: number): VisibleCampaignCreationWizardProgress => ({
    visible: true,
    progress,
    segments,
});

export const HIDDEN_PROGRESS: CampaignCreationWizardProgress = { visible: false };

export const getCampaignCreationWizardProgress = ({
    activeStep,
    campaignTarget,
    vacanciesCount,
    vacanciesIsLoading,
    isFiltered,
    selectedVacancyId,
    selectedProductType,
    selectedCpcMode,
}: WizardProgressParams): CampaignCreationWizardProgress => {
    if (activeStep === CampaignCreationWizardStep.PromotionObject) {
        if (campaignTarget === 'vacancy') {
            if (!vacanciesIsLoading && vacanciesCount === 0 && !isFiltered) {
                return HIDDEN_PROGRESS;
            }
            return VISIBLE_PROGRESS(1, 3);
        }

        return VISIBLE_PROGRESS(1, selectedVacancyId === null ? 4 : 3);
    }

    if (selectedProductType === CampaignProductType.Cpc && selectedCpcMode === 'flexible') {
        return VISIBLE_PROGRESS(2, 5);
    }

    return VISIBLE_PROGRESS(2, 3);
};

export const getCampaignCreationWizardAuctionProgress = (): VisibleCampaignCreationWizardProgress =>
    VISIBLE_PROGRESS(3, 3);

export const getCampaignCreationWizardQuickCpcProgress = (): VisibleCampaignCreationWizardProgress =>
    VISIBLE_PROGRESS(3, 3);

// exp_PORTFOLIO-58018_next_line
export const getCampaignCreationWizardVrProgress = (): VisibleCampaignCreationWizardProgress => VISIBLE_PROGRESS(3, 3);

export const getCampaignCreationWizardManualCpcProgress = (
    currentStep: number,
    source?: CampaignCreationWizardSource | null
): VisibleCampaignCreationWizardProgress => {
    if (source === CampaignCreationWizardSource.ExternalLink) {
        return VISIBLE_PROGRESS(currentStep + 2, 4);
    }

    return VISIBLE_PROGRESS(currentStep + 3, 5);
};

export const getCampaignCreationWizardStepForSettingsBack = (source?: CampaignCreationWizardSource | null): number =>
    source === CampaignCreationWizardSource.ExternalLink
        ? CampaignCreationWizardStep.PromotionObject
        : CampaignCreationWizardStep.PromotionType;

export const createCampaignCreationWizardBreadcrumbClickHandler =
    (openBackAlert: (activeStep: number) => void, activeStep: number): ((event: MouseEvent) => void) =>
    (event) => {
        event.preventDefault();
        openBackAlert(activeStep);
    };
